<template>
  <div
    class="mega-nav"
    :class="`
      mega-nav--${layout}
      mega-nav--scribble-${scribble}
    `"
  >
    <ul
      class="mega-nav__navigation"
    >
      <li
        v-for="tier2 of items"
        :key="tier2.handle"
        class="mega-nav__list"
      >
        <a
          v-if="isClickable(tier2.url)"
          class="mega-nav__link mega-nav__link-title text-other-label-bold"
          :class="{'mega-nav__link-view-all': checkIfLinkIsviewAll(tier2)}"
          :href="tier2.url"
          :style="getLinkStyle(tier2.title)"
          v-text="tier2.title"
        />

        <h3
          v-else
          class="mega-nav__link text-other-label-bold"
          :style="getLinkStyle(tier2.title)"
          v-text="tier2.title"
        />
        <sup
          v-if="tier2?.flagTag"
          class="flag-tag"
          :style="{ color: tier2?.flagColor }"
        >{{ tier2?.flagTag }}</sup>

        <ul v-if="itemHasChildren(tier2)">
          <li
            v-for="tier3 of tier2.items"
            :key="tier3.handle"
          >
            <a
              v-if="isClickable(tier3.url)"
              class="mega-nav__link text-body-m-regular"
              :class="{'mega-nav__link-view-all': checkIfLinkIsviewAll(tier3)}"
              :href="tier3.url"
              :style="getLinkStyle(tier2.title, tier3.title)"
              v-html="tier3.title"
            />

            <span
              v-else
              class="mega-nav__link text-body-m-regular"
              :style="getLinkStyle(tier2.title)"
              v-html="tier3.title"
            />
            <sup
              v-if="tier3?.flagTag"
              class="flag-tag"
              :style="{ color: tier3?.flagColor }"
            >{{ tier3?.flagTag }}</sup>
          </li>
        </ul>
      </li>
    </ul>

    <nav-signpost
      v-if="signpost"
      :signpost="signpost"
      block-class="mega-nav"
      is-tabbable
    />

    <div
      v-else-if="highlights"
      class="mega-nav__highlights"
    >
      <span
        class="mega-nav__highlights-title text-other-label-bold"
        v-text="$string('general.site_header.highlights')"
      />

      <nav-highlights
        :highlights="highlights"
        block-class="mega-nav"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Statically rendered list of links with supporting image.
 *
 * @param {Array} items - Navigation items, with a handle, title and url.
 * @param {Boolean|Number} scribble - Scribble to render.
 * @param {Object} signpost - Signpost section block.
 * @param {Array} highlights - Highlights section block.
 * @param {Array} navLinkColors - Links that have been assigned colors.
 *
 */
import NavHighlights from '~async/nav-highlights/nav-highlights'
import NavSignpost from '~async/nav-signpost/nav-signpost'

export default {
  name: 'MegaNav',

  components: {
    NavHighlights,
    NavSignpost,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    scribble: {
      type: [Boolean, Number],
      default: false,
    },
    signpost: {
      type: [Boolean, Object],
      default: false,
    },
    highlights: {
      type: [Boolean, Array],
      default: false,
    },
    navLinkColors: {
      type: Array,
      default: () => [],
    },
  },

  computed: {

    /**
     * Compute layout based on existence of blocks.
     * @returns {String}
     */
    layout() {
      if (this.signpost) {
        return 'signpost'
      }

      if (this.highlights.length) {
        return 'highlights'
      }

      return 'columns'
    },
  },

  methods: {

    /**
     * Returns if the item has a URL to render as a link.
     * The "#" symbol is used as an override.
     * @param {Object} url - The item URL to test.
     * @returns {Boolean}
     */
    isClickable(url) {
      return url !== '#'
    },

    /**
     * Returns if the given item has children items.
     * @param {Object} item - The item to test.
     * @returns {Boolean}
     */
    itemHasChildren(item) {
      return item.items && item.items.length
    },

    /**
     * Check if link is view all.
     * @param {Object} link - Link object.
     * @returns {Boolean}
     */
    checkIfLinkIsviewAll(link) {
      const viewAllString = this.$string('general.menu_drawer.view_all')
      if (!viewAllString || !link?.title) {
        return false
      }
      return link.title.toLowerCase().includes(viewAllString.toLowerCase())
    },

    /**
     * Get style attribute with color by link title(s).
     * @param {String} tier2Title - Tier 2 link title.
     * @param {String} tier3Title - Tier 3 link title.
     * @returns {String}
     */
    getLinkStyle(tier2Title, tier3Title) {
      let titleToMatch = this.sanitizeTitle(tier2Title)

      if (tier3Title) {
        titleToMatch += `|${this.sanitizeTitle(tier3Title)}`
      }

      const matchedColor = this.navLinkColors.find(color => {
        return color.toLowerCase().split(':')[0] === titleToMatch
      })

      return matchedColor ? `color: ${matchedColor.split(':')[1]}` : ''
    },

    /**
     * Sanitize title to remove any HTML and prevent case-sensitivity.
     * @param {String} title - Tier 1 link title.
     * @returns {String}
     */
    sanitizeTitle(title) {
      const regex = new RegExp(/<[^>]*>(?<inner>[^<]*)<\/[^>]*>/, 'gu')

      return title.replace(regex, (match, group) => group).toLowerCase()
    },
  },
}
</script>
